import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Styles from './BrandInsightsDashboard.module.scss';
import {
  FiArrowLeft,
  FiBarChart2,
  FiRefreshCw,
} from 'react-icons/fi';
import { getRequest, postRequest } from 'helpers/axios';
import { success as successToast, error as errorToast } from 'helpers/toaster';

// Import section components
import {
  BrandVoiceProfile,
  ContentDNA,
  TopPerformers,
  PlatformDeepDive,
  CrossPlatformInsights,
  BrandUnderstandingSummary
} from './components';

const BrandInsightsDashboard = () => {
  const { brandId } = useParams();
  const navigate = useNavigate();

  const [brandProfile, setBrandProfile] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [hasFullAnalysis, setHasFullAnalysis] = useState(false);

  useEffect(() => {
    fetchBrandData();
  }, [brandId]);

  const fetchBrandData = async () => {
    setLoading(true);
    try {
      // First get the brand profile
      const profileResponse = await getRequest(`brand_intelligence/profiles/${brandId}`);
      if (profileResponse.data.success) {
        setBrandProfile(profileResponse.data.data.brand_profile);
      }

      // Then try to get cached insights
      try {
        const insightsResponse = await getRequest(`brand_profiles/${brandId}/brand_intelligence/insights`);
        if (insightsResponse.data.success && insightsResponse.data.data) {
          setInsights(insightsResponse.data.data);
          setHasFullAnalysis(insightsResponse.data.has_full_analysis || false);
        }
      } catch (insightErr) {
        // Insights not available yet, that's okay
        console.log('No cached insights available');
      }
    } catch (error) {
      console.error('Error fetching brand data:', error);
      errorToast('Failed to load brand data');
    } finally {
      setLoading(false);
    }
  };

  const runFullAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await postRequest(
        `brand_profiles/${brandId}/brand_intelligence/full_analysis`,
        { force_refresh: true }
      );

      if (response.data.success) {
        setInsights(response.data.data);
        setHasFullAnalysis(true);
        successToast('Brand analysis complete!');
      } else {
        errorToast(response.data.error || 'Analysis failed');
      }
    } catch (error) {
      console.error('Error running analysis:', error);
      errorToast(error.response?.data?.error || 'Failed to run analysis');
    } finally {
      setAnalyzing(false);
    }
  };

  // Check if we have any platforms with data
  const hasPlatformData = insights?.platforms_analyzed?.length > 0 ||
    insights?.platform_deep_dives && Object.keys(insights.platform_deep_dives).length > 0;

  if (loading) {
    return (
      <div className={Styles.dashboardContainer}>
        <div className={Styles.loadingState}>
          <FiRefreshCw className={Styles.spinning} />
          <p>Loading brand insights...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={Styles.dashboardContainer}>
      {/* Header */}
      <div className={Styles.header}>
        <button className={Styles.backBtn} onClick={() => navigate('/brand')}>
          <FiArrowLeft /> Back to Brands
        </button>
        <div className={Styles.headerTitle}>
          <FiBarChart2 />
          <h1>Brand Insights: {brandProfile?.brand_name || 'Unknown Brand'}</h1>
        </div>
        <button
          className={Styles.refreshBtn}
          onClick={runFullAnalysis}
          disabled={analyzing}
        >
          <FiRefreshCw className={analyzing ? Styles.spinning : ''} />
          {analyzing ? 'Analyzing...' : hasFullAnalysis ? 'Refresh Analysis' : 'Run Full Analysis'}
        </button>
      </div>

      {/* Main Content */}
      {!hasPlatformData && !insights ? (
        <div className={Styles.emptyState}>
          <FiBarChart2 />
          <h2>No Social Data Available</h2>
          <p>Fetch posts from your social channels in the Brand Intelligence setup to see insights.</p>
          <button onClick={() => navigate(`/brand-intelligence/${brandId}`)}>
            Go to Brand Setup
          </button>
        </div>
      ) : !insights || !hasFullAnalysis ? (
        <div className={Styles.emptyState}>
          <FiBarChart2 />
          <h2>Ready to Analyze</h2>
          <p>Run full analysis to see comprehensive insights about your brand's voice, content DNA, and what works for you.</p>
          <button onClick={runFullAnalysis} disabled={analyzing}>
            {analyzing ? (
              <>
                <FiRefreshCw className={Styles.spinning} />
                Analyzing your content...
              </>
            ) : (
              'Run Full Analysis'
            )}
          </button>
        </div>
      ) : (
        <div className={Styles.insightsContainer}>
          {/* Section 1: Brand Voice Profile */}
          <BrandVoiceProfile
            voiceData={insights.unified_voice}
            hookPatterns={getAggregatedHookPatterns(insights.platform_deep_dives)}
            ctaPatterns={getAggregatedCtaPatterns(insights.platform_deep_dives)}
          />

          {/* Section 2: Content DNA */}
          <ContentDNA
            pillars={insights.aggregated_pillars}
            platformDeepDives={insights.platform_deep_dives}
            successPatterns={insights.success_patterns}
          />

          {/* Section 3: Top Performers */}
          <TopPerformers
            topPerformers={insights.top_performers}
            successPatterns={insights.success_patterns}
          />

          {/* Section 4: Platform Deep Dives */}
          <PlatformDeepDive
            platformDeepDives={insights.platform_deep_dives}
          />

          {/* Section 5: Cross-Platform Insights */}
          <CrossPlatformInsights
            crossPlatform={insights.cross_platform}
            platformsAnalyzed={insights.platforms_analyzed}
          />

          {/* Section 6: Brand Understanding Summary */}
          <BrandUnderstandingSummary
            totalPosts={insights.total_posts_analyzed}
            platformCount={insights.platform_count}
            platformsAnalyzed={insights.platforms_analyzed}
            hasVoiceData={!!insights.unified_voice?.personality_traits?.length}
            hasPillars={!!insights.aggregated_pillars?.length}
            hasTopPerformers={!!insights.top_performers?.length}
            hasCrossPlatform={!!insights.cross_platform?.consistency_summary}
            brandId={brandId}
          />
        </div>
      )}
    </div>
  );
};

// Helper to aggregate hook patterns across platforms
const getAggregatedHookPatterns = (platformDeepDives) => {
  if (!platformDeepDives) return null;

  const allDistributions = {};
  const allExamples = {};

  Object.values(platformDeepDives).forEach(data => {
    const hooks = data.hook_patterns || {};
    const dist = hooks.distribution || {};
    const examples = hooks.examples || {};

    Object.entries(dist).forEach(([type, pct]) => {
      allDistributions[type] = (allDistributions[type] || 0) + pct;
    });

    Object.entries(examples).forEach(([type, example]) => {
      if (!allExamples[type]) allExamples[type] = example;
    });
  });

  // Average the distributions
  const platformCount = Object.keys(platformDeepDives).length;
  Object.keys(allDistributions).forEach(key => {
    allDistributions[key] = Math.round(allDistributions[key] / platformCount);
  });

  // Find dominant style
  const dominantStyle = Object.entries(allDistributions)
    .sort((a, b) => b[1] - a[1])[0]?.[0] || 'mixed';

  return {
    distribution: allDistributions,
    examples: allExamples,
    dominant_style: dominantStyle
  };
};

// Helper to aggregate CTA patterns across platforms
const getAggregatedCtaPatterns = (platformDeepDives) => {
  if (!platformDeepDives) return null;

  const allCtas = [];
  let softTotal = 0;
  let hardTotal = 0;
  let platformCount = 0;

  Object.values(platformDeepDives).forEach(data => {
    const cta = data.cta_patterns || {};

    if (cta.common_ctas) {
      allCtas.push(...cta.common_ctas);
    }

    if (cta.soft_vs_hard_ratio) {
      softTotal += cta.soft_vs_hard_ratio.soft || 50;
      hardTotal += cta.soft_vs_hard_ratio.hard || 50;
      platformCount++;
    }
  });

  // Dedupe CTAs
  const uniqueCtas = [...new Set(allCtas)];

  return {
    common_ctas: uniqueCtas.slice(0, 6),
    cta_placement: 'end',
    soft_vs_hard_ratio: platformCount > 0
      ? {
          soft: Math.round(softTotal / platformCount),
          hard: Math.round(hardTotal / platformCount)
        }
      : { soft: 50, hard: 50 }
  };
};

export default BrandInsightsDashboard;
